/**
 * anonymizationService.js
 * ────────────────────────
 * Production-grade anonymisation for the data retention engine. Implements
 * the techniques GDPR requires when pay equity statistics are retained
 * past the personal-data retention window.
 *
 * Why this matters
 * ────────────────
 * GDPR Recital 26 says data is no longer "personal" when "the data subject
 * is not or no longer identifiable". A naive "we removed the name" is NOT
 * sufficient, someone with a salary band, gender, department, and seniority
 * can often be re-identified from combinations alone (1990s landmark study:
 * 87% of US population uniquely identifiable from {ZIP, birthdate, sex}).
 *
 * The two techniques implemented here
 * ────────────────────────────────────
 *   1. K-anonymity, every record in the released dataset is indistinguishable
 *      from at least k-1 other records on the quasi-identifier columns. Cells
 *      with fewer than k subjects are SUPPRESSED. Industry default k=5.
 *
 *   2. Pseudonymisation (Art. 4(5)), replace a direct identifier with a
 *      cryptographic hash so longitudinal analysis works (same person across
 *      time periods) but no identifier is revealed. Uses a tenant-scoped
 *      salt so hashes can't be cross-correlated between customers.
 *
 * What this is NOT
 * ────────────────
 * This is not differential privacy (no calibrated noise injection) and not
 * a substitute for a DPO review. The k=5 threshold is a sensible default but
 * some regulators (EDPS, German BfDI) have argued for k=10 or higher in
 * employment contexts where re-identification carries discrimination risk.
 * Tenants can override the threshold per their DPO's guidance.
 */

const SALT_KEY = (tenantId) => `clearequal.anonSalt.${tenantId}`;

/**
 * Get or generate a tenant-scoped salt for pseudonymisation hashes.
 * Salts are stored once per tenant and never rotated unless a DPO explicitly
 * triggers re-pseudonymisation (which breaks longitudinal linkage by design).
 */
function getTenantSalt(tenantId) {
  if (!tenantId) return 'no-tenant-salt';
  try {
    let salt = localStorage.getItem(SALT_KEY(tenantId));
    if (!salt) {
      // Generate 32 random bytes hex-encoded
      const bytes = new Uint8Array(32);
      crypto.getRandomValues(bytes);
      salt = Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
      localStorage.setItem(SALT_KEY(tenantId), salt);
    }
    return salt;
  } catch {
    return 'fallback-salt-' + tenantId;
  }
}

/**
 * Pseudonymise a direct identifier (email, employee ID) using SHA-256 with
 * a tenant-scoped salt. Returns the first 16 hex chars (64 bits, enough
 * uniqueness for typical tenant sizes, much smaller than full digest).
 *
 * Same input + same tenant = same pseudonym (longitudinal analysis works).
 * Same input + different tenant = different pseudonym (no cross-tenant join).
 */
export async function pseudonymise(value, tenantId) {
  if (!value) return null;
  const salt = getTenantSalt(tenantId);
  const data = new TextEncoder().encode(`${salt}::${String(value).toLowerCase().trim()}`);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashHex = Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return 'p_' + hashHex.slice(0, 16);
}

/**
 * Synchronous fallback for environments where SubtleCrypto is unavailable
 * (older browsers, some test runners). Not cryptographically strong but
 * preserves the API contract for the UAT.
 */
export function pseudonymiseSync(value, tenantId) {
  if (!value) return null;
  const salt = getTenantSalt(tenantId);
  const input = `${salt}::${String(value).toLowerCase().trim()}`;
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash + input.charCodeAt(i)) | 0;
  }
  return 'p_' + Math.abs(hash).toString(16).padStart(8, '0');
}

/**
 * Apply k-anonymity to an array of records.
 *
 * @param {Array<Object>} records - raw records, each with `quasiIdentifiers`
 *                                  used for grouping (e.g. ['gender', 'department', 'level']).
 * @param {Array<string>} quasiIdentifiers - column names to use for grouping
 * @param {number} k - minimum group size; cells with fewer subjects get suppressed
 * @returns {{ released: Array, suppressed: Array, summary: Object }}
 *
 * Released records: groups with >= k members, identifiers stripped.
 * Suppressed records: groups with < k members, returned for the audit log
 * so the DPO knows what was withheld and why.
 */
export function applyKAnonymity(records, quasiIdentifiers = [], k = 5) {
  if (!Array.isArray(records) || records.length === 0) {
    return { released: [], suppressed: [], summary: { totalRecords: 0, releasedCount: 0, suppressedCount: 0, k } };
  }

  // Group records by their quasi-identifier signature
  const groups = new Map();
  for (const rec of records) {
    const sig = quasiIdentifiers.map((q) => String(rec[q] ?? '')).join('|');
    if (!groups.has(sig)) groups.set(sig, []);
    groups.get(sig).push(rec);
  }

  const released = [];
  const suppressed = [];

  for (const [sig, group] of groups) {
    if (group.length >= k) {
      // Group is large enough, release with quasi-identifiers but no direct identifiers
      for (const rec of group) {
        const stripped = {};
        for (const qi of quasiIdentifiers) stripped[qi] = rec[qi];
        // Carry through statistical fields (salary, etc.) that are NOT direct identifiers
        for (const key of Object.keys(rec)) {
          if (quasiIdentifiers.includes(key)) continue;
          if (DIRECT_IDENTIFIERS.has(key)) continue; // never release these
          stripped[key] = rec[key];
        }
        released.push(stripped);
      }
    } else {
      // Suppressed, log the count but not the individuals
      suppressed.push({
        signature: sig,
        groupSize: group.length,
        reason: `Group below k=${k} threshold, re-identification risk too high`,
      });
    }
  }

  return {
    released,
    suppressed,
    summary: {
      totalRecords: records.length,
      releasedCount: released.length,
      suppressedCount: records.length - released.length,
      groupCount: groups.size,
      releasedGroupCount: groups.size - suppressed.length,
      suppressedGroupCount: suppressed.length,
      k,
    },
  };
}

// Direct identifiers that must NEVER appear in a "released" anonymised set.
// Anything not listed is considered a quasi-identifier or statistical attribute.
const DIRECT_IDENTIFIERS = new Set([
  'email', 'employeeEmail', 'subjectEmail', 'first_name', 'last_name',
  'name', 'employeeName', 'phone', 'address', 'employee_id', 'employeeId',
  'national_id', 'tax_id', 'iban', 'profile_photo_url', 'date_of_birth',
]);

/**
 * Anonymise pay equity statistics for retention. This is the main entry
 * point called by the auto-purge engine when a category's policy is
 * `action: 'anonymise'` rather than `action: 'delete'`.
 *
 * @param {Array} payRecords - records with at minimum {gender, department, level, salary}
 * @param {string} tenantId
 * @param {Object} options - { k, quasiIdentifiers }
 */
export function anonymisePayStats(payRecords, tenantId, options = {}) {
  const k = options.k || 5;
  const qi = options.quasiIdentifiers || ['gender', 'department', 'level'];
  return applyKAnonymity(payRecords, qi, k);
}

/**
 * For DPO documentation: produce a human-readable explanation of what
 * happens to a category's data when its retention window elapses.
 * Used in the Admin Console retention table and in data-subject explanations.
 */
export function explainCategoryFate(category, action) {
  if (action === 'delete') {
    return 'Records are permanently removed. The data subject can confirm via the purge audit log.';
  }
  if (action === 'anonymise') {
    return 'Direct identifiers removed; remaining attributes released only for cell sizes ≥ k (default k=5). ' +
           'Smaller cells are suppressed entirely. The result is no longer personal data under GDPR Art. 4(1).';
  }
  if (action === 'pseudonymise') {
    return 'Direct identifiers replaced with tenant-scoped cryptographic hashes (Art. 4(5)). The data ' +
           'remains personal data and stays in scope of GDPR, but is protected against direct linkage.';
  }
  return 'Unknown action.';
}
